/**
 * What a store purchase that did not end cleanly is called in Sentry, and which
 * tags it carries (#6244).
 *
 * Both purchase paths in `iap.service.ts` used to report every such failure under
 * one name per path, with the reason only in the report's context. Sentry stores
 * that context as a breadcrumb, which cannot be searched or grouped by. So a store
 * outage, a by-design refusal, the phone's own store call failing and the phone
 * being offline were one Sentry issue, and a day on which every Android
 * confirmation failed did not stand out on the issue list.
 *
 * The split follows what a person would DO about each failure:
 *  - the store could not be asked: its own name, so an outage is its own line on
 *    the issue list and can carry an alert;
 *  - any other refusal from our server: the old name, with a `store_refusal` tag;
 *  - something threw: a thrown name, with `purchase_step` and `network` tags.
 *
 * Tag values are a fixed, small set, as `SentryReportTags` in problem-kind.ts
 * requires. The exact refusal code still travels in the context.
 *
 * Kept free of Angular so `store-purchase-failure-report.test.ts` can run it under
 * mocha. Why the split looks like this: design-docs/iap-subscriptions.md, "Client
 * purchase-failure telemetry".
 */
import type { SentryReportTags } from './problem-kind';
import { StorePurchaseStep } from './store-purchase-flow';
import { isNetworkError } from './cloud-functions-errors';
import { describeError } from '@cf-shared/error-describe';
import {
  isStoreCouldNotCheckCode,
  STORE_VALIDATE_ACCOUNT_MISMATCH,
  STORE_VALIDATE_REQUEST_REFUSED,
} from '@cf-shared/store-validate-errors';

/** Which purchase path is reporting. Each keeps its own Sentry names. */
export enum StorePurchaseRail {
  /** someone buying personal Premium (`IAPService.purchaseAsync`) */
  Personal = 'personal',
  /** a coach buying a coaching tier (`IAPService.purchaseCoachTierAsync`) */
  CoachTier = 'coach-tier',
}

/** Everything `trackSilentProblem` needs for one report. */
export interface StorePurchaseFailureReport {
  name: string;
  context: Record<string, unknown>;
  tags: SentryReportTags;
}

interface RailNames {
  /** the store could not be asked */
  storeUnavailable: string;
  /** any other refusal from our server */
  refused: string;
  /** something threw */
  threw: string;
}

// `NativePurchaseValidateFailed` and both coach-tier names already existed before
// #6244; renaming them would split their history in Sentry for no gain
const RAIL_NAMES: Record<StorePurchaseRail, RailNames> = {
  [StorePurchaseRail.Personal]: {
    storeUnavailable: 'NativePurchaseStoreUnavailable',
    refused: 'NativePurchaseValidateFailed',
    threw: 'NativePurchaseThrew',
  },
  [StorePurchaseRail.CoachTier]: {
    storeUnavailable: 'CoachTierStoreUnavailable',
    refused: 'CoachTierValidateFailed',
    threw: 'CoachTierPurchaseThrew',
  },
};

/** the `store_refusal` tag values. Anything not listed becomes `other`. */
enum StoreRefusalTag {
  AlreadyLinked = 'already_linked',
  RequestRefused = 'store_request_refused',
  AccountMismatch = 'account_mismatch',
  Other = 'other',
}

/** Turn a refusal code into a tag value from the fixed set. */
function storeRefusalTag(code: string | undefined): StoreRefusalTag {
  // 'already_linked' is still a bare string at every call site, see store-validate-errors.ts
  if (code === 'already_linked') return StoreRefusalTag.AlreadyLinked;
  if (code === STORE_VALIDATE_REQUEST_REFUSED) return StoreRefusalTag.RequestRefused;
  // the account-mismatch code is a whole sentence, so it must never be a tag value itself
  if (code === STORE_VALIDATE_ACCOUNT_MISMATCH) return StoreRefusalTag.AccountMismatch;
  return StoreRefusalTag.Other;
}

/** The report for a purchase our server answered with an error code. */
export function describeRefusalReport(rail: StorePurchaseRail, code: string | undefined): StorePurchaseFailureReport {
  const names = RAIL_NAMES[rail];
  const context = { error: code };
  // if the store could not be asked, it gets its own name: this is the outage signal
  if (isStoreCouldNotCheckCode(code)) {
    return { name: names.storeUnavailable, context, tags: {} };
  }
  return { name: names.refused, context, tags: { store_refusal: storeRefusalTag(code) } };
}

/** The report for a purchase in which something threw before any answer came back. */
export function describeThrownReport(rail: StorePurchaseRail, error: unknown, step: StorePurchaseStep): StorePurchaseFailureReport {
  return {
    name: RAIL_NAMES[rail].threw,
    context: { errorMessage: describeError(error), step },
    tags: { purchase_step: step, network: isNetworkError(error) ? 'yes' : 'no' },
  };
}
